// Typed fetch client for the SeaState console API.
//
// Same-origin, cookie-authenticated. Every call returns a discriminated
// Result — success carries typed data, failure carries the HTTP status and a
// human error string. Nothing throws across this boundary: a dead endpoint or
// a malformed body degrades into `{ ok: false, ... }`, never an exception into
// a blank page. JSON responses are `unknown` and narrowed through small guards
// (the openmeteo.ts style), so a partial or wrong-shaped payload fails closed.

// --- Response types ---------------------------------------------------------

export interface User {
  name: string;
  email: string;
  client: string;
  /** "admin" | "member" — what this person may do INSIDE their own client.
   *  Nothing to do with the admin area: the client's primary account is 'admin'
   *  by seeding. Older servers omit it; absent therefore means "member". */
  role?: string;
  /** Whether this is a SeaState principal rather than a client user. Decides
   *  whether the Admin entry is RENDERED and nothing more — every /api/admin/*
   *  endpoint re-reads the column server-side on every request, so a client that
   *  lies about this gains a menu item and no data. Older servers omit it;
   *  absent therefore means false. */
  staff?: boolean;
  /** Which console this client gets — "fleet" | "thermal". Decided server-side
   *  from clients.console and reported through the EFFECTIVE client, so a
   *  principal switched to another client gets that client's console. Absent
   *  from an older server; absent means "fleet". */
  console?: string;
  /** Stable client slug, for branding. Not the display name, which has no
   *  unique constraint behind it. */
  client_key?: string;
  /** True when `client` is NOT this user's own — a principal viewing another
   *  client. The header must say so; see own_client. */
  viewing?: boolean;
  own_client?: string;
  /** Server-side per-user prefs (users.prefs JSONB), passed through verbatim.
   *  Deliberately untyped here: each consumer narrows the slice it owns (the
   *  console narrows prefs.home), so an unknown key from a newer server never
   *  breaks an older client. */
  prefs: unknown;
}

export interface ApiVessel {
  id: number;
  key: string;
  name: string;
  loa_m: number;
  beam_m: number;
  roll_period_s: number;
  draft_m: number;
  cruise_kn: number;
  max_kn: number;
  dp: boolean;
  day_only: boolean;
  nearshore: boolean;
  gear: string;
  operation_ids: number[];
}

export interface ApiOperation {
  id: number;
  /** Preset family key (matches a `defaults` entry), or null for bespoke ops. */
  kind: string | null;
  label: string;
}

/** One entry in the registry `defaults` map, keyed by operation kind. */
export interface ApiDefault {
  label: string;
  wave_m: number;
  wind_kn: number;
  task_hours: number;
  squall_sensitivity: unknown;
  dp_station: unknown;
}

export type ApiDefaults = Record<string, ApiDefault>;

export interface ApiLimit {
  vessel_id: number;
  operation_id: number;
  wave_m: number;
  wind_kn: number;
  task_hours: number;
  updated_at: string;
}

export interface ApiRegistry {
  vessels: ApiVessel[];
  operations: ApiOperation[];
  defaults: ApiDefaults;
  limits: ApiLimit[];
}

/** The three editable numbers on a vessel × operation limit row. */
export interface LimitValues {
  wave_m: number;
  wind_kn: number;
  task_hours: number;
}

/**
 * The writable particulars of a vessel (create/update). Nullable measurements
 * are `null` when unspecified so the server stores NULL rather than 0.
 */
export interface VesselInput {
  name: string;
  loa_m: number | null;
  beam_m: number | null;
  roll_period_s: number | null;
  draft_m: number | null;
  cruise_kn: number | null;
  max_kn: number | null;
  dp: boolean;
  day_only: boolean;
  nearshore: boolean;
  gear: string | null;
}

export type Result<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      status: number;
      error: string;
      /** Machine-readable code from the server, when it sent one: the
       *  operations-password gate's 403s and its throttled 429. */
      code?: string;
      /** `Retry-After` in whole seconds, when the server asked us to come back
       *  later rather than answering. It arrives on the throttles' 429 and on
       *  the 503 the argon2 semaphore sheds with (server/ratelimit.go), and it
       *  is the difference between "sign-in failed" and a wait the operator can
       *  actually act on. */
      retryAfter?: number;
    };

// The operations-password gate (server/opsgate.go). Spec mutations — limits,
// the operations catalog, vessel-operation links — carry the client's
// operations password in this header when one is set; a refusal carries one of
// these codes, distinct from every other error so the page can open its confirm
// dialog instead of pattern-matching prose.
const OPS_PASSWORD_HEADER = "X-Ops-Password";
export const OPS_PASSWORD_REQUIRED = "ops_password_required";
export const OPS_PASSWORD_INVALID = "ops_password_invalid";
/** Too many wrong ones: the gate refuses to look at the password for a while.
 *  A 429 rather than a 403, and it carries Retry-After. */
export const OPS_PASSWORD_THROTTLED = "ops_password_throttled";

/**
 * True when a failed Result is the operations-password gate refusing — for any
 * of its three reasons. All three keep the confirm dialog on screen with the
 * operator's edit intact, which is what this predicate decides; only the dialog
 * cares WHICH one, because only `ops_password_invalid` says the password was
 * wrong. The throttled 429 is the gate declining to check it at all.
 */
export function isOpsGateRefusal<T>(res: Result<T>): boolean {
  if (res.ok) return false;
  if (res.status === 403) {
    return res.code === OPS_PASSWORD_REQUIRED || res.code === OPS_PASSWORD_INVALID;
  }
  return res.status === 429 && res.code === OPS_PASSWORD_THROTTLED;
}

/**
 * True when a failure is the server shedding load rather than judging a
 * credential: the argon2 semaphore was full, so no hash ran and nothing about
 * the password was learned (server/ratelimit.go, errHashBusy).
 *
 * Keyed on 503 + Retry-After, because that pairing is what the capacity path
 * sends and the other 503 on these routes — "database unavailable" — does not.
 * The server sends no `code` here; matching on the error prose instead is the
 * brittleness the gate's codes exist to avoid.
 */
export function isServerBusy<T>(res: Result<T>): boolean {
  return !res.ok && res.status === 503 && res.retryAfter !== undefined;
}

// --- JSON narrowing ---------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function bool(value: unknown): boolean {
  return value === true;
}

function numArray(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((v): v is number => typeof v === "number" && Number.isFinite(v))
    : [];
}

function narrowUser(raw: unknown): User | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const name = str(r.name);
  const email = str(r.email);
  const client = str(r.client);
  if (name === null || email === null || client === null) return null;
  // role and staff are OPTIONAL on the type and must still be carried. Building
  // the object field by field is the right instinct — a server that grows a key
  // cannot surprise the client — but a field the type declares and this function
  // forgets is worse than an untyped passthrough: it type-checks everywhere and
  // is undefined at runtime.
  //
  // That is not hypothetical. `staff` gates whether the Admin entry renders, and
  // `role` did before it. Both were declared, both were read, and neither was
  // ever copied here — so the entry never appeared for anybody, and nothing
  // failed loudly enough to say so. It was found by opening the page.
  return {
    name,
    email,
    client,
    role: str(r.role) ?? undefined,
    staff: r.staff === true,
    console: str(r.console) ?? undefined,
    client_key: str(r.client_key) ?? undefined,
    viewing: r.viewing === true,
    own_client: str(r.own_client) ?? undefined,
    prefs: r.prefs ?? null,
  };
}

/** Login / me responses wrap the user as `{ "user": {...} }`. */
function narrowUserEnvelope(raw: unknown): User | null {
  return narrowUser(asRecord(raw)?.user);
}

function narrowVessel(raw: unknown): ApiVessel | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const id = num(r.id);
  const key = str(r.key);
  const name = str(r.name);
  if (id === null || key === null || name === null) return null;
  return {
    id,
    key,
    name,
    loa_m: num(r.loa_m) ?? 0,
    beam_m: num(r.beam_m) ?? 0,
    roll_period_s: num(r.roll_period_s) ?? 0,
    draft_m: num(r.draft_m) ?? 0,
    cruise_kn: num(r.cruise_kn) ?? 0,
    max_kn: num(r.max_kn) ?? 0,
    dp: bool(r.dp),
    day_only: bool(r.day_only),
    nearshore: bool(r.nearshore),
    gear: str(r.gear) ?? "",
    operation_ids: numArray(r.operation_ids),
  };
}

function narrowOperation(raw: unknown): ApiOperation | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const id = num(r.id);
  const label = str(r.label);
  if (id === null || label === null) return null;
  return { id, label, kind: str(r.kind) };
}

function narrowDefault(raw: unknown): ApiDefault | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const label = str(r.label);
  const wave_m = num(r.wave_m);
  const wind_kn = num(r.wind_kn);
  const task_hours = num(r.task_hours);
  if (label === null || wave_m === null || wind_kn === null || task_hours === null) {
    return null;
  }
  return {
    label,
    wave_m,
    wind_kn,
    task_hours,
    squall_sensitivity: r.squall_sensitivity,
    dp_station: r.dp_station,
  };
}

function narrowLimit(raw: unknown): ApiLimit | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const vessel_id = num(r.vessel_id);
  const operation_id = num(r.operation_id);
  const wave_m = num(r.wave_m);
  const wind_kn = num(r.wind_kn);
  const task_hours = num(r.task_hours);
  if (
    vessel_id === null ||
    operation_id === null ||
    wave_m === null ||
    wind_kn === null ||
    task_hours === null
  ) {
    return null;
  }
  return {
    vessel_id,
    operation_id,
    wave_m,
    wind_kn,
    task_hours,
    updated_at: str(r.updated_at) ?? "",
  };
}

function narrowRegistry(raw: unknown): ApiRegistry | null {
  const r = asRecord(raw);
  if (r === null) return null;
  if (!Array.isArray(r.vessels) || !Array.isArray(r.operations)) return null;

  const vessels = r.vessels
    .map(narrowVessel)
    .filter((v): v is ApiVessel => v !== null);
  const operations = r.operations
    .map(narrowOperation)
    .filter((o): o is ApiOperation => o !== null);
  const limits = Array.isArray(r.limits)
    ? r.limits.map(narrowLimit).filter((l): l is ApiLimit => l !== null)
    : [];

  const defaults: ApiDefaults = {};
  const defaultsRec = asRecord(r.defaults);
  if (defaultsRec !== null) {
    for (const kind of Object.keys(defaultsRec)) {
      const d = narrowDefault(defaultsRec[kind]);
      if (d !== null) defaults[kind] = d;
    }
  }

  return { vessels, operations, defaults, limits };
}

// --- Request plumbing -------------------------------------------------------

/** `Retry-After` as whole seconds. The server writes it as a positive integer
 *  count and never as the HTTP-date form the RFC also allows
 *  (server/ratelimit.go, setRetryAfter), so anything else is treated as absent
 *  rather than guessed at — a wait we cannot read is better stated as "shortly"
 *  than as a number we invented. */
function retryAfterSeconds(res: Response): number | undefined {
  const raw = res.headers.get("Retry-After");
  if (raw === null) return undefined;
  const seconds = Number(raw.trim());
  return Number.isInteger(seconds) && seconds > 0 ? seconds : undefined;
}

/** The failed half of a Result, built from a non-OK Response: the server's
 *  error string and machine code when it sent them, and the wait when it asked
 *  for one. */
async function failure(res: Response): Promise<Result<never>> {
  const out: {
    ok: false;
    status: number;
    error: string;
    code?: string;
    retryAfter?: number;
  } = { ok: false, status: res.status, error: `Request failed (HTTP ${res.status}).` };
  const wait = retryAfterSeconds(res);
  if (wait !== undefined) out.retryAfter = wait;
  try {
    const rec = asRecord(await res.json());
    const err = rec ? str(rec.error) : null;
    const code = rec ? str(rec.code) : null;
    if (err !== null) out.error = err;
    if (code !== null) out.code = code;
  } catch {
    // Non-JSON error body — the generic message above stands.
  }
  return out;
}

function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

/** Attach the operations password to a request, when the caller has one. */
function withOpsPassword(init: RequestInit, opsPassword?: string): RequestInit {
  if (opsPassword === undefined || opsPassword === "") return init;
  return {
    ...init,
    headers: { ...init.headers, [OPS_PASSWORD_HEADER]: opsPassword },
  };
}

async function requestJson<T>(
  url: string,
  init: RequestInit,
  parse: (raw: unknown) => T | null,
): Promise<Result<T>> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    return { ok: false, status: 0, error: "Network error — could not reach the server." };
  }
  if (!res.ok) {
    return failure(res);
  }
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { ok: false, status: res.status, error: "Malformed response from server." };
  }
  const data = parse(raw);
  if (data === null) {
    return { ok: false, status: res.status, error: "Unexpected response shape." };
  }
  return { ok: true, data };
}

async function requestVoid(url: string, init: RequestInit): Promise<Result<void>> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    return { ok: false, status: 0, error: "Network error — could not reach the server." };
  }
  if (!res.ok) {
    return failure(res);
  }
  return { ok: true, data: undefined };
}

// --- Public API -------------------------------------------------------------

export function me(): Promise<Result<User>> {
  return requestJson("/api/me", { method: "GET" }, narrowUserEnvelope);
}

export function login(email: string, password: string): Promise<Result<User>> {
  return requestJson("/api/login", jsonInit("POST", { email, password }), narrowUserEnvelope);
}

export function logout(): Promise<Result<void>> {
  return requestVoid("/api/logout", { method: "POST" });
}

/** One account's arrival record. `sign_ins` counts session rows, which is a
 *  lower bound — see server/admin_usage.go. */
export interface AdminUsageAccount {
  email: string;
  name: string;
  staff: boolean;
  sign_ins: number;
  active_days: number;
  first: string | null;
  last: string | null;
}

export interface AdminUsageClient {
  id: number;
  name: string;
  accounts: AdminUsageAccount[];
  days: { date: string; count: number }[];
  adoption: {
    vessels: number;
    operations: number;
    links: number;
    /** Pairs carrying the CLIENT's own limit. Below `links` means the rest are
     *  still graded on our seeded numbers. */
    tuned: number;
    last_tuned_at: string | null;
    feedback: number;
    prefs_touched: number;
  };
}

export interface AdminUsage {
  clients: AdminUsageClient[];
  /** Days covered by the daily series. */
  window: number;
}

/** Admin-only. Trusted verbatim: the endpoint is ours, behind requireAdmin, and
 *  a shape guard here would only restate the Go structs. */
export function getAdminUsage(): Promise<Result<AdminUsage>> {
  return requestJson("/api/admin/usage", { method: "GET" }, (v) => v as AdminUsage);
}

/** Whose account a setup link belongs to. Named before anything is proven, so
 *  the claim page can greet the right person rather than asking for an email it
 *  already knows. A bad, spent or expired token is one undifferentiated 404. */
export interface SetupAccount {
  email: string;
  name: string;
}

function narrowSetupAccount(v: unknown): SetupAccount | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  if (typeof o.email !== "string" || typeof o.name !== "string") return null;
  return { email: o.email, name: o.name };
}

export function getSetupAccount(token: string): Promise<Result<SetupAccount>> {
  return requestJson(
    `/api/setup?token=${encodeURIComponent(token)}`,
    { method: "GET" },
    narrowSetupAccount,
  );
}

/** Set the password this account was created without, spend the token, and sign
 *  in — the server opens the session, so a success lands already authenticated. */
export function setupPassword(token: string, password: string): Promise<Result<User>> {
  return requestJson("/api/setup", jsonInit("POST", { token, password }), narrowUserEnvelope);
}

export function getRegistry(): Promise<Result<ApiRegistry>> {
  return requestJson("/api/registry", { method: "GET" }, narrowRegistry);
}

/** Merge a partial prefs object into the signed-in user's users.prefs JSONB.
 *  A patch, not a replacement: {clock: "utc"} must not clobber prefs.home. */
export function putPrefs(patch: Record<string, unknown>): Promise<Result<void>> {
  return requestVoid("/api/prefs", jsonInit("PUT", patch));
}

// The eight spec mutators below take an optional operations password (the
// opsgate flow in pages/vessels.ts supplies it); everything else does not.
//
// The spec is not only the limits. A vessel's particulars are inputs to the
// same verdicts — loa_m, beam_m, draft_m and roll_period_s feed the seakeeping
// maths, dp/day_only/nearshore gate whole operations — so createVessel and
// updateVessel move a go/no-go call exactly as putLimit does. And deleteVessel
// is the sharpest of the eight: the vessel FKs cascade, so removing a hull
// destroys every saved limit tuned for it. Gating the numbers while leaving the
// row that owns them open was a bypass, not a gap.

export function putLimit(
  vesselId: number,
  opId: number,
  values: LimitValues,
  opsPassword?: string,
): Promise<Result<ApiLimit>> {
  return requestJson(
    `/api/limits/${vesselId}/${opId}`,
    withOpsPassword(jsonInit("PUT", values), opsPassword),
    narrowLimit,
  );
}

export function deleteLimit(
  vesselId: number,
  opId: number,
  opsPassword?: string,
): Promise<Result<void>> {
  return requestVoid(
    `/api/limits/${vesselId}/${opId}`,
    withOpsPassword({ method: "DELETE" }, opsPassword),
  );
}

export function createOperation(
  label: string,
  kind?: string,
  opsPassword?: string,
): Promise<Result<ApiOperation>> {
  const body: { label: string; kind?: string } = { label };
  if (kind !== undefined) body.kind = kind;
  return requestJson(
    "/api/operations",
    withOpsPassword(jsonInit("POST", body), opsPassword),
    narrowOperation,
  );
}

export function deleteOperation(id: number, opsPassword?: string): Promise<Result<void>> {
  return requestVoid(
    `/api/operations/${id}`,
    withOpsPassword({ method: "DELETE" }, opsPassword),
  );
}

export function setVesselOperations(
  vesselId: number,
  ids: number[],
  opsPassword?: string,
): Promise<Result<void>> {
  return requestVoid(
    `/api/vessels/${vesselId}/operations`,
    withOpsPassword(jsonInit("PUT", { operation_ids: ids }), opsPassword),
  );
}

export function createVessel(
  input: VesselInput,
  opsPassword?: string,
): Promise<Result<ApiVessel>> {
  return requestJson(
    "/api/vessels",
    withOpsPassword(jsonInit("POST", input), opsPassword),
    narrowVessel,
  );
}

export function updateVessel(
  id: number,
  input: VesselInput,
  opsPassword?: string,
): Promise<Result<ApiVessel>> {
  return requestJson(
    `/api/vessels/${id}`,
    withOpsPassword(jsonInit("PUT", input), opsPassword),
    narrowVessel,
  );
}

export function deleteVessel(id: number, opsPassword?: string): Promise<Result<void>> {
  return requestVoid(
    `/api/vessels/${id}`,
    withOpsPassword({ method: "DELETE" }, opsPassword),
  );
}

// --- Sound Velocity Profile (SVP) -------------------------------------------

/** One SVP grid cell. `p` rows are [depth_m, tempC, salPSU, soundSpeed_ms]. */
export interface SvpCell {
  lat: number;
  lon: number;
  p: number[][];
  /** [top_m, bottom_m, dropC] or null when well-mixed. */
  th: number[] | null;
  /** mixed-layer depth (m) or null. */
  mld: number | null;
  /** sound-channel (SOFAR) axis depth (m). */
  ca: number;
  /** surface sound speed (m/s). */
  cs: number;
  /**
   * The MODEL's seabed at this cell — the deepest level that still held water.
   * Null when the column ran to the product's own floor (5,727.9 m), where all
   * that is known is "at least this deep".
   *
   * Prefer this over comparing the profile's deepest level against a charted
   * depth: they are different surveys and disagree by a bias, not by noise.
   * Optional because a grid baked before 2026-08-14 has no `sb`.
   */
  sb?: number | null;
}

/**
 * WOA23 monthly climatology for the cell — the "usual" curve beside the forecast
 * (CMEMS) and the measured (Argo) one. `levels` are [depth_m, tempC, salPSU]
 * triples, deliberately WITHOUT sound speed: the client derives SV under the
 * selected equation so all three curves share one equation rather than mixing
 * whatever formula each source happened to ship. Optional — absent when the cell
 * falls outside the climatology grid, which is a clean absence, never an error.
 */
export interface SvpClimatology {
  /** Zero-padded month, e.g. "07". */
  month: string;
  /** Human month name for the legend/provenance, e.g. "July". */
  monthName: string;
  /** [depth_m, tempC, salPSU] rows; only rows with three finite values survive. */
  levels: number[][];
  source: string;
  cellLat: number;
  cellLon: number;
}

export interface SvpResult {
  date: string;
  source: string;
  cell: SvpCell;
  /** WOA23 climatology for the cell, or null when the block is absent/empty. */
  climatology: SvpClimatology | null;
}

/**
 * Nearest SVP cell for a point. Resolves to `{ ok: true, data: null }` when the
 * point is outside the pre-fetched regions (the endpoint 204s), so callers can
 * treat "no coverage" as a clean absence rather than an error.
 */
export async function fetchSvp(lat: number, lon: number): Promise<Result<SvpResult | null>> {
  let res: Response;
  try {
    res = await fetch(`/api/svp?lat=${lat}&lon=${lon}`, { credentials: "same-origin" });
  } catch {
    return { ok: false, status: 0, error: "Network error — could not reach the server." };
  }
  if (res.status === 204) return { ok: true, data: null }; // outside coverage
  if (!res.ok) return failure(res);
  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { ok: false, status: res.status, error: "Malformed response from server." };
  }
  const r = asRecord(raw);
  const cell = asRecord(r?.cell);
  if (r === null || cell === null || !Array.isArray(cell.p)) {
    return { ok: false, status: res.status, error: "Unexpected response shape." };
  }
  const num2 = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

  // Optional WOA23 climatology. Drop malformed levels per row (non-finite depth/T/S)
  // rather than coercing to 0 the way the model cell does — a spurious 0-value row
  // would draw a false reference curve. No surviving level = no block, treated as a
  // clean absence (the point is off the climatology grid), never an error.
  const clim = asRecord(r.climatology);
  let climatology: SvpClimatology | null = null;
  if (clim !== null && Array.isArray(clim.levels)) {
    const levels = (clim.levels as unknown[])
      .filter(Array.isArray)
      .map((row) => (row as unknown[]).slice(0, 3).map((v) => (typeof v === "number" ? v : Number.NaN)))
      .filter((row) => row.length >= 3 && row.every(Number.isFinite));
    if (levels.length > 0) {
      climatology = {
        month: str(clim.month) ?? "",
        monthName: str(clim.monthName) ?? "",
        levels,
        source: str(clim.source) ?? "",
        cellLat: num2(clim.cellLat),
        cellLon: num2(clim.cellLon),
      };
    }
  }

  return {
    ok: true,
    data: {
      date: str(r.date) ?? "",
      source: str(r.source) ?? "",
      cell: {
        lat: num2(cell.lat),
        lon: num2(cell.lon),
        p: (cell.p as unknown[]).filter(Array.isArray).map((row) => (row as unknown[]).map(num2)),
        th: Array.isArray(cell.th) ? (cell.th as unknown[]).map(num2) : null,
        mld: num(cell.mld),
        ca: num2(cell.ca),
        cs: num2(cell.cs),
        // The three states are distinct and a reader depends on it: a NUMBER is
        // the model's seabed, `null` means the column ran to the product's own
        // floor ("at least this deep"), and ABSENT means this grid was baked
        // before the field existed and knows nothing either way. Collapsing the
        // last two prints "deeper than 5,728 m" over a 900 m site.
        sb: "sb" in cell ? num(cell.sb) : undefined,
      },
      climatology,
    },
  };
}

// --- feedback ---------------------------------------------------------------

/** One operator report, as stored. `context` is whatever the console knew when
 *  it was written and is deliberately untyped here — the shape changes as the
 *  console does, and a report with an unexpected key must still display. */
export interface ApiFeedback {
  id: number;
  created_at: string;
  message: string;
  author: string;
  context: unknown;
}

function narrowFeedback(raw: unknown): ApiFeedback | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const id = num(r.id);
  const created_at = str(r.created_at);
  const message = str(r.message);
  if (id === null || created_at === null || message === null) return null;
  return { id, created_at, message, author: str(r.author) ?? "", context: r.context };
}

function narrowFeedbackList(raw: unknown): ApiFeedback[] | null {
  const r = asRecord(raw);
  if (r === null || !Array.isArray(r.feedback)) return null;
  const out: ApiFeedback[] = [];
  for (const item of r.feedback) {
    const f = narrowFeedback(item);
    if (f !== null) out.push(f);
  }
  return out;
}

export function getFeedback(): Promise<Result<ApiFeedback[]>> {
  return requestJson("/api/feedback", { method: "GET" }, narrowFeedbackList);
}

/** Submit one report. `context` travels verbatim — send whatever locates it. */
export function sendFeedback(message: string, context: unknown): Promise<Result<ApiFeedback>> {
  return requestJson("/api/feedback", jsonInit("POST", { message, context }), narrowFeedback);
}

export function deleteFeedback(id: number): Promise<Result<void>> {
  return requestVoid(`/api/feedback/${id}`, { method: "DELETE" });
}
